/**
 * Unit conversion for the catalog admin screen.
 *
 * Three units meet on that screen and confusing any two of them is a billing
 * bug rather than a display bug:
 *
 *   · MINOR UNITS of the row's own currency — the actual charge. Sen, cents,
 *     yen, fils. Also exactly what Stripe's `unit_amount` takes.
 *   · BILLING-CURRENCY MICRO — the plan's included allowance and the credit a
 *     pack grants. 10^-6 of the MAJOR unit, because one LLM turn is far below
 *     a minor unit; see `microToInput` below, which takes no currency for
 *     exactly that reason. This said "USD MICRODOLLARS" until 2026-08-18, four
 *     migrations after 0177 re-denominated both — and a header claiming the
 *     wrong unit is what makes a `$` at the render site look correct.
 *   · BYTES — the storage quota.
 *
 * Extracted from CatalogAdmin.tsx so the conversions can be tested directly.
 * The exponent always comes from `currencyMinorUnitExponent`, the same source
 * `parseMajorToMinor` uses — a literal `/100` here is a 100× error on JPY (zero
 * decimals) and a 10× one on BHD (three).
 */
import {
  parseMajorToMinor, currencyMinorUnitExponent, currencyMinorToBillingMicro,
} from '@papan/types'

export const USD_MICRO = 1_000_000
export const GB = 1024 ** 3

/**
 * Major units → minor, allowing ZERO.
 *
 * `parseMajorToMinor` rejects 0 because its original caller was a customer
 * purchase, where a zero amount is always a bug. A zero PRICE is legitimate on
 * the catalog screen — it is what the `free` plan costs — so zero is handled
 * here rather than by loosening the shared parser for every caller.
 */
export function parsePrice(text: string, currency: string): number | null {
  const cleaned = text.trim().replace(/,/g, '')
  if (/^0(\.0+)?$/.test(cleaned)) return 0
  return parseMajorToMinor(cleaned, currency)
}

/**
 * Minor units → a plain editable major-unit string (no symbol, no grouping).
 *
 * The inverse of `parsePrice`, using the SAME exponent source — round-tripping
 * a price through the pair must not change it, which it would if display and
 * input disagreed about how many decimals a currency has.
 */
export function priceToInput(minor: number, currency: string): string {
  const exponent = currencyMinorUnitExponent(currency)
  return exponent === 0 ? String(minor) : (minor / 10 ** exponent).toFixed(exponent)
}

/** USD microdollars → an editable dollar string. */
export const usdToInput = (micro: number): string => String(micro / USD_MICRO)

/** A typed dollar amount → USD microdollars, or null when unusable. Zero is
 *  valid: a plan may include no AI budget at all. */
export function inputToUsd(text: string): number | null {
  const n = Number(text.trim())
  if (text.trim() === '' || !Number.isFinite(n) || n < 0) return null
  return Math.round(n * USD_MICRO)
}

/**
 * Billing-currency MICRO → an editable major-unit string, and back.
 *
 * ── NO CURRENCY ARGUMENT, AND THAT IS THE POINT ──────────────────────────
 *
 * A micro unit is 10^-6 of the MAJOR unit by definition, in every currency —
 * unlike a MINOR unit, whose count per major is 100 for MYR, 1 for JPY and
 * 1000 for BHD. So this conversion is exactly `/ 1e6` everywhere, and reaching
 * for `currencyMinorUnitExponent` here would be the error, not the safeguard.
 *
 * That is precisely why the allowance columns are stored in micro: a single LLM
 * turn is far below one minor unit and would round to zero.
 */
const BILLING_MICRO = 1_000_000

export const microToInput = (micro: number): string => String(micro / BILLING_MICRO)

/** A typed major-unit amount → billing micro, or null when unusable. Zero is
 *  valid: a plan may include no AI allowance at all. */
export function inputToMicro(text: string): number | null {
  const n = Number(text.trim())
  if (text.trim() === '' || !Number.isFinite(n) || n < 0) return null
  return Math.round(n * BILLING_MICRO)
}

/**
 * A metered-rate draft field is VALID when blank (inherit) or a non-negative
 * finite number. The ONE rule `FeaturePricingEditor.save` (EDIT mode) and
 * `confirmDraft` (CREATE mode) both apply, so a present-but-unparseable rate
 * (an operator typing "2x") is refused in both places instead of being
 * silently dropped in one — extracted here so create and edit can't drift.
 */
export function isValidRateField(rate: string): boolean {
  const s = rate.trim()
  if (s === '') return true
  const n = Number(s)
  return Number.isFinite(n) && n >= 0
}

/**
 * A model-price draft row's status — shared by `LlmPricing.save` (EDIT) and
 * `confirmDraft` (CREATE), for the same anti-drift reason as `isValidRateField`.
 *   `'blank'`   — both sides empty ⇒ inherit the built-in rate (skip).
 *   `'valid'`   — parses to non-negative amounts (an empty side reads as 0,
 *                 matching the edit-mode editor).
 *   `'invalid'` — present but unparseable/negative ⇒ refuse (never half-write).
 */
export function modelPriceFieldStatus(input: string, output: string): 'blank' | 'valid' | 'invalid' {
  const inBlank = input.trim() === ''
  const outBlank = output.trim() === ''
  if (inBlank && outBlank) return 'blank'
  const inp = Number(input)
  const out = Number(output)
  if (!Number.isFinite(inp) || inp < 0 || !Number.isFinite(out) || out < 0) return 'invalid'
  return 'valid'
}

export const bytesToGb = (bytes: number): string => String(Math.round((bytes / GB) * 100) / 100)

export function gbToBytes(text: string): number | null {
  const n = Number(text.trim())
  if (text.trim() === '' || !Number.isFinite(n) || n < 0) return null
  return Math.round(n * GB)
}

/**
 * A blank optional integer field means "unset" (null), NOT zero — an empty
 * concurrency cap is unlimited, while 0 would be "no agents at all".
 * `undefined` means the text was not a usable integer.
 */
export function parseOptionalInt(text: string): number | null | undefined {
  const s = text.trim()
  if (s === '') return null
  const n = Number(s)
  if (!Number.isInteger(n) || n < 0) return undefined
  return n
}

/**
 * Credit granted for a price, in BILLING-CURRENCY MICRO.
 *
 * Codex F13 (round 19) — this used to divide the price by the credit
 * rate, mirroring the retired `minorUnitsToCreditUsdMicro`. That is
 * exactly the arithmetic 0177 removed: a purchase now grants its own
 * FACE VALUE in the deployment's billing currency, rate-free.
 * `credits.ts:117` says so in a "Do not reintroduce a rate here"
 * comment; this preview drifted from it and was rendering ~1.8× the
 * real credit (90,909,090 vs 50,000,000 for RM50 at rate 550), making
 * `changed` permanently true on every pack row.
 *
 * MIRRORS `currencyMinorToBillingMicro` in `@papan/types` — the same
 * boundary the server uses in `catalog-routes.ts:1040/1088`, pinned
 * by `catalog-routes.test.ts:614`. The server is still the authority
 * and writes the frozen value; this only previews what saving will
 * produce, and now matches the server exactly.
 *
 * The `rate` parameter is deliberately gone from the signature — a
 * caller that still had it would be encoding the retired conversion,
 * and the next drift class waiting to happen. The two call sites in
 * `CatalogAdmin.tsx` were updated in the same commit.
 */
export function previewCreditBillingMicro(priceMinor: number, currency: string): number {
  return currencyMinorToBillingMicro(priceMinor, currency)
}

/**
 * A plan that costs nothing is never sold.
 *
 * It therefore has no Stripe Price BY DESIGN, and "not published" is its
 * correct, permanent state rather than a missing step. Shared by the catalog
 * screen's row badge and its unpublished-rows banner so the two cannot
 * disagree — they did, and the banner told an operator to publish the very plan
 * the row two lines below marked "Not sold".
 *
 * Keyed on price, not on `id === 'free'`: a deployment may add its own
 * zero-price tier, and it would be just as unsellable.
 *
 * ── UNPRICED IS NOT FREE, SO IT IS NOT "NEVER SOLD" EITHER ──────────────
 *
 * `null` here means the plan has no `billing_plan_prices` row in the
 * deployment's currency. That is a catalogue gap the operator must close, not a
 * permanent property of the plan — so it returns FALSE and the row keeps
 * reporting itself as unpublished, which is the state that gets fixed. Reading
 * absence as "costs nothing" would mark a mispriced Pro "Not sold" and hide it
 * from the banner whose whole job is to surface it.
 */
export const isNeverSold = (price: { basePriceMinor: number } | null): boolean =>
  price != null && price.basePriceMinor === 0

/**
 * Billing-currency MICRO → a localized currency string (e.g. "RM50.00").
 *
 * Codex F13 (round 19) — this was `fmtUsd(micro)` with a hardcoded
 * `currency: 'USD'` label. It sat next to the pack credit preview,
 * which is `billing_micro` in the DEPLOYMENT currency, so a pack row
 * on an MYR deployment rendered "$50.00" over a RM 50 face value.
 * Now takes the currency and asks Intl for the right symbol; the
 * `en-US` locale is kept — a `pt-BR` symbol on the operator page is
 * a separate localization decision, and this keeps the previous
 * layout width predictable.
 */
export const fmtBillingMicro = (micro: number, currency: string): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(micro / BILLING_MICRO)
